import { useWindowSize } from '@leedpulse-monorepo/hooks';
import { Button, Flex, message, Pagination, Table, Tag } from 'antd';
import type { TableColumnsType } from 'antd';
import { countryCodes } from '@leedpulse-monorepo/utils';
import { useNavigate } from 'react-router-dom';
import { numberProductsAsString } from '@leedpulse-monorepo/enums';
import { INumberView } from '../../../domain/interfaces/INumberView';
import { advancedOnRow } from '../../../core/utils/commonUtils';

// Images
import sakariImg from '../../../assets/images/icons/sakari.png';
import twilioImg from '../../../assets/images/icons/twilio.png';
import callrImg from '../../../assets/images/icons/callr.png';
import { renderChannelOption } from '@leedpulse-monorepo/enums-ui';
import { NUMBERS_PAGE_PATH } from '../../pages/numbers/NumbersPage';

import styles from './NumbersList.module.css';
import { CopyOutlined } from '@ant-design/icons';

type NumbersListRow = Partial<INumberView> & { objectID: string; separator?: string };

// Marks the characters of a typo tolerated hit that do not match the query.
const renderSenderMismatch = (sender: string, query: string) => {
  let prefix = 0;
  while (prefix < sender.length && prefix < query.length && sender[prefix] === query[prefix]) prefix += 1;

  let suffix = 0;
  while (
    suffix < sender.length - prefix
    && suffix < query.length - prefix
    && sender[sender.length - 1 - suffix] === query[query.length - 1 - suffix]
  ) suffix += 1;

  return (
    <>
      {sender.slice(0, prefix)}
      <span className={styles.numbersListMismatch}>{sender.slice(prefix, sender.length - suffix)}</span>
      {sender.slice(sender.length - suffix)}
    </>
  );
};

interface NumbersListProps {
  numbers: INumberView[];
  query: string;
  exactNbHits: number;
  loading: boolean;
  page: number;
  nbHits: number;
  hitsPerPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (newPageSize: number) => void;
}

const NumbersList: React.FC<NumbersListProps> = ({ numbers, query, exactNbHits, loading, page, nbHits, hitsPerPage, onPageChange, onPageSizeChange }) => {
  const windowSize = useWindowSize();
  const navigate = useNavigate();

  const isApproaching = (oNumber: NumbersListRow) =>
    query.trim().length > 0 && (oNumber._rankingInfo?.nbTypos ?? 0) > 0;

  const approachingNumbers = numbers.filter(isApproaching);
  const exactNumbers = numbers.filter((oNumber) => !isApproaching(oNumber));

  const dataSource: NumbersListRow[] = query.trim().length > 0
    ? [
      ...(exactNumbers.length > 0
        ? [{ objectID: 'separator-exact', separator: `${exactNbHits} résultats exacts` }, ...exactNumbers]
        : []),
      ...(approachingNumbers.length > 0
        ? [{ objectID: 'separator-approaching', separator: `${nbHits - exactNbHits} résultats similaires` }, ...approachingNumbers]
        : []),
    ]
    : numbers;

  const senderColumn = {
    title: 'Numéro',
    dataIndex: 'sender',
    key: 'sender',
    render: (sender: string, record: NumbersListRow) => (
      <Flex align="center" gap={4}>
        <span>
          {isApproaching(record) ? renderSenderMismatch(sender, query.trim()) : sender}
        </span>
        <Button
          className={styles.numbersListCopyButton}
          type="text" icon={<CopyOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            window.navigator.clipboard.writeText(sender);
            message.success('Numéro copié dans le presse-papiers');
          }}
        />
      </Flex>
    ),
  };

  const availabilityColumn = {
    title: 'Disponibilité',
    dataIndex: 'available',
    key: 'available',
    render: (available: boolean) => (
      available
        ? <Tag color="green">Disponible</Tag>
        : <Tag color="red">Attribué</Tag>
    ),
  };

  const columns: TableColumnsType<NumbersListRow> =
    windowSize.width > 576
      ? [
        {
          title: 'Nom',
          render: (oNumber) => (
            <>
              {renderChannelOption(oNumber.channel)}
              <span style={{ marginLeft: '4px', whiteSpace: 'nowrap' }}>{oNumber.group_name}</span>
            </>
          ),
        },
        senderColumn,
        availabilityColumn,
        {
          title: 'Pays',
          dataIndex: 'country',
          key: 'country',
          render: (country) => countryCodes.find(c => c.name === country)?.flag + ' ' + countryCodes.find(c => c.name === country)?.shortName,
        },
        {
          title: 'Type',
          dataIndex: 'channel',
          key: 'channel',
        },
        {
          title: 'Fournisseur',
          dataIndex: 'provider',
          key: 'provider',
          render: (provider) => (
            <>
              {provider === 'sakari' && <img src={sakariImg} alt={provider} style={{ height: '18px' }} />}
              {provider === 'twilio' && <img src={twilioImg} alt={provider} style={{ height: '18px' }} />}
              {provider === 'callr' && <img src={callrImg} alt={provider} style={{ height: '14px' }} />}
            </>
          ),
        },
        {
          title: 'Produit',
          dataIndex: 'product',
          key: 'product',
          render: (product) => numberProductsAsString(product),
        },
      ]
      : [senderColumn, availabilityColumn];

  // Just a normal row with special style and no onclick
  const columnsWithSeparator: TableColumnsType<NumbersListRow> = columns.map((column, index) => ({
    ...column,
    onCell: (record: NumbersListRow) => (
      record.separator ? { colSpan: index === 0 ? columns.length : 0 } : {}
    ),
    render: (value: unknown, record: NumbersListRow, rowIndex: number) => {
      if (record.separator) return index === 0 ? record.separator : null;
      return 'render' in column && column.render
        ? column.render(value, record, rowIndex)
        : (value as React.ReactNode);
    },
  }));

  const handlePageSizeChange = (_current: number, newPageSize: number) => {
    onPageSizeChange?.(newPageSize);
  };

  const paginationProps = {
    current: page + 1,
    total: nbHits,
    pageSize: hitsPerPage,
    onChange: (p: number) => onPageChange(p - 1),
    onShowSizeChange: handlePageSizeChange,
    showSizeChanger: true,
    simple: true,
    pageSizeOptions: [20, 50, 100, 200],
    disabled: loading,
  };

  return (
    <>
      <div className={styles.numbersListPagination}>
        <Pagination {...paginationProps} />
      </div>
      <Table
        pagination={false}
        loading={loading}
        columns={columnsWithSeparator}
        rowKey={(record) => record.objectID}
        rowClassName={(record) =>
          record.separator
            ? styles.numbersListRowSeparator
            : record.available ? styles.numbersListRowAvailable : styles.numbersListRowAssigned
        }
        dataSource={dataSource}
        onRow={(record) =>
          record.separator
            ? {}
            : advancedOnRow({
              url: `${NUMBERS_PAGE_PATH}/${record.objectID}`,
              navigate,
              className: 'table-row',
            })
        }
      />
      <div className={styles.numbersListPagination}>
        <Pagination {...paginationProps} />
      </div>
    </>
  );
};

export default NumbersList;
